/**
 * Allowlisted issue-navigation resolver (spec-13 §6).
 *
 * The server never sends URLs; it sends a typed intent (`kind` + params) and
 * this resolver builds the final in-app path. Unknown kinds, malformed UUIDs,
 * or missing route params degrade to the nearest authorized batch section —
 * never to `/` and never to a broken path.
 */

import type { IssueNavigationIntent, IssueTarget, ResolvedNavigation } from './types';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isUuid = (v: unknown): v is string => typeof v === 'string' && UUID_RE.test(v);

/** Section slugs `monthly_batch` intents may point at via `?section=`. */
const BATCH_SECTION_ALLOWLIST = new Set([
  'opening-balance',
  'imports',
  'inbound',
  'sorting',
  'dismantling',
  'outbounds',
  'inventory',
  'evidence',
  'mass-balance',
  'audit-package',
  'exceptions',
  'approvals',
  // Financial-spine sections used by blocker-category navigation
  // (BLOCKER_CATEGORY_SECTIONS) — real routes in app/router.tsx.
  'first-valuation',
  'valuation/monthly-material',
  'valuation/getit-purchase-price',
  'valuation/supplier-purchase-price',
  'settlement',
  'reports',
]);

/** Target-type slugs allowed in `?targetType=` queries (outbounds/evidence/approvals). */
const TARGET_TYPE_ALLOWLIST = new Set([
  'shipment',
  'outbound',
  'secondary_transfer',
  'sales_outbound',
  'inbound_lot',
  'inbound_package',
  'dismantling_session',
  'material',
  'inventory_movement',
  'monthly_batch',
]);

const param = (intent: IssueNavigationIntent, name: string): string | undefined => {
  const raw = intent.params?.[name];
  return typeof raw === 'string' && raw.length > 0 ? raw : undefined;
};

/** Free-text query values (search/highlight) — bounded and URL-encoded. */
const textParam = (intent: IssueNavigationIntent, name: string): string | undefined => {
  const raw = param(intent, name);
  if (!raw) return undefined;
  return raw.slice(0, 120);
};

const withQuery = (path: string, entries: Array<[string, string | undefined]>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of entries) {
    if (value) search.set(key, value);
  }
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
};

const unavailable = (reasonKey: string, fallbackBatchId?: string): ResolvedNavigation => ({
  ok: false,
  reasonKey,
  fallbackPath: isUuid(fallbackBatchId) ? `/batches/${fallbackBatchId}` : undefined,
});

/**
 * Resolve a typed navigation intent to an in-app path.
 *
 * `fallbackBatchId` is the batch the issue belongs to; it powers the
 * "nearest authorized batch section" degradation when the intent itself is
 * unusable.
 */
export function resolveIssueNavigation(
  intent: IssueNavigationIntent | null | undefined,
  fallbackBatchId?: string | null,
): ResolvedNavigation {
  const fallback = fallbackBatchId ?? undefined;
  if (!intent || typeof intent.kind !== 'string') {
    return unavailable('efs.issues.navigation.no_target', fallback);
  }

  const batchId = param(intent, 'batch_id');

  switch (intent.kind) {
    case 'monthly_batch': {
      if (!isUuid(batchId)) return unavailable('efs.issues.navigation.invalid_target', fallback);
      const section = param(intent, 'section');
      if (section && BATCH_SECTION_ALLOWLIST.has(section)) {
        return { ok: true, path: `/batches/${batchId}/${section}` };
      }
      return { ok: true, path: `/batches/${batchId}` };
    }
    case 'batch_opening_balance': {
      if (!isUuid(batchId)) return unavailable('efs.issues.navigation.invalid_target', fallback);
      return { ok: true, path: `/batches/${batchId}/opening-balance` };
    }
    case 'batch_mass_balance': {
      if (!isUuid(batchId)) return unavailable('efs.issues.navigation.invalid_target', fallback);
      const exceptionId = param(intent, 'exception_id');
      return {
        ok: true,
        path: withQuery(`/batches/${batchId}/mass-balance`, [
          ['exceptionId', isUuid(exceptionId) ? exceptionId : undefined],
          ['category', textParam(intent, 'category')],
        ]),
      };
    }
    case 'batch_exceptions': {
      if (!isUuid(batchId)) return unavailable('efs.issues.navigation.invalid_target', fallback);
      const exceptionId = param(intent, 'exception_id');
      return {
        ok: true,
        path: withQuery(`/batches/${batchId}/exceptions`, [
          ['exceptionId', isUuid(exceptionId) ? exceptionId : undefined],
        ]),
      };
    }
    case 'batch_inbound_lot': {
      const lotId = param(intent, 'lot_id');
      if (!isUuid(batchId) || !isUuid(lotId)) {
        return unavailable('efs.issues.navigation.invalid_target', fallback);
      }
      return { ok: true, path: `/batches/${batchId}/inbound/${lotId}` };
    }
    case 'batch_sorting': {
      if (!isUuid(batchId)) return unavailable('efs.issues.navigation.invalid_target', fallback);
      const lotId = param(intent, 'lot_id');
      return {
        ok: true,
        path: withQuery(`/batches/${batchId}/sorting`, [
          ['lotId', isUuid(lotId) ? lotId : undefined],
        ]),
      };
    }
    case 'batch_dismantling': {
      if (!isUuid(batchId)) return unavailable('efs.issues.navigation.invalid_target', fallback);
      const sessionId = param(intent, 'session_id');
      return {
        ok: true,
        path: withQuery(`/batches/${batchId}/dismantling`, [
          ['sessionId', isUuid(sessionId) ? sessionId : undefined],
        ]),
      };
    }
    case 'batch_outbounds': {
      if (!isUuid(batchId)) return unavailable('efs.issues.navigation.invalid_target', fallback);
      const targetType = param(intent, 'target_type');
      const targetId = param(intent, 'target_id');
      return {
        ok: true,
        path: withQuery(`/batches/${batchId}/outbounds`, [
          [
            'targetType',
            targetType && TARGET_TYPE_ALLOWLIST.has(targetType) ? targetType : undefined,
          ],
          ['targetId', isUuid(targetId) ? targetId : undefined],
        ]),
      };
    }
    case 'batch_inventory': {
      if (!isUuid(batchId)) return unavailable('efs.issues.navigation.invalid_target', fallback);
      const materialId = param(intent, 'material_id');
      const movementId = param(intent, 'movement_id');
      return {
        ok: true,
        path: withQuery(`/batches/${batchId}/inventory`, [
          ['materialId', isUuid(materialId) ? materialId : undefined],
          ['movementId', isUuid(movementId) ? movementId : undefined],
        ]),
      };
    }
    case 'batch_evidence': {
      if (!isUuid(batchId)) return unavailable('efs.issues.navigation.invalid_target', fallback);
      const targetType = param(intent, 'target_type');
      const targetId = param(intent, 'target_id');
      return {
        ok: true,
        path: withQuery(`/batches/${batchId}/evidence`, [
          [
            'targetType',
            targetType && TARGET_TYPE_ALLOWLIST.has(targetType) ? targetType : undefined,
          ],
          ['targetId', isUuid(targetId) ? targetId : undefined],
        ]),
      };
    }
    case 'batch_audit_package': {
      if (!isUuid(batchId)) return unavailable('efs.issues.navigation.invalid_target', fallback);
      return { ok: true, path: `/batches/${batchId}/audit-package` };
    }
    case 'batch_approvals': {
      if (!isUuid(batchId)) return unavailable('efs.issues.navigation.invalid_target', fallback);
      const targetType = param(intent, 'target_type');
      const targetId = param(intent, 'target_id');
      return {
        ok: true,
        path: withQuery(`/batches/${batchId}/approvals`, [
          [
            'targetType',
            targetType && TARGET_TYPE_ALLOWLIST.has(targetType) ? targetType : undefined,
          ],
          ['targetId', isUuid(targetId) ? targetId : undefined],
        ]),
      };
    }
    case 'master_incoming_item':
      return {
        ok: true,
        path: withQuery('/masters/incoming-items', [
          ['q', textParam(intent, 'q')],
          [
            'highlight',
            isUuid(param(intent, 'record_id')) ? param(intent, 'record_id') : undefined,
          ],
        ]),
      };
    case 'master_material':
      return {
        ok: true,
        path: withQuery('/masters/materials', [
          ['q', textParam(intent, 'q')],
          [
            'highlight',
            isUuid(param(intent, 'record_id')) ? param(intent, 'record_id') : undefined,
          ],
        ]),
      };
    case 'master_destination':
      return {
        ok: true,
        path: withQuery('/masters/destinations', [
          ['q', textParam(intent, 'q')],
          [
            'highlight',
            isUuid(param(intent, 'record_id')) ? param(intent, 'record_id') : undefined,
          ],
        ]),
      };
    case 'supplier_batch': {
      if (!isUuid(batchId)) return unavailable('efs.issues.navigation.invalid_target', fallback);
      return { ok: true, path: `/supplier/batches/${batchId}` };
    }
    default:
      return unavailable('efs.issues.navigation.unsupported_target', fallback);
  }
}

/**
 * Resolve the navigation for a presentation target, including the
 * batch-section fallback (spec-13 §4.2: a missing route degrades to the
 * nearest authorized batch section, never `/`).
 */
export function resolveTargetNavigation(
  target: IssueTarget | null | undefined,
): ResolvedNavigation {
  return resolveIssueNavigation(target?.navigation ?? null, target?.monthly_batch_id ?? undefined);
}
